import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import toast from "react-hot-toast";
import {
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiInfo,
  FiHelpCircle,
  FiX,
} from "react-icons/fi";

/**
 * Global SweetAlert-compatible helper.
 *
 * Important: confirm/prompt dialogs are rendered through ONE dedicated
 * portal root instead of react-hot-toast. This guarantees that rapid clicks,
 * route re-renders, React StrictMode and repeated handlers can never stack
 * multiple confirmation cards on top of each other.
 *
 * Normal success/error/info messages still use react-hot-toast.
 */

const ICON_COMPONENTS = {
  success: FiCheckCircle,
  error: FiXCircle,
  warning: FiAlertTriangle,
  info: FiInfo,
  question: FiHelpCircle,
};

const TOAST_ICON = {
  success: "✓",
  error: "×",
  warning: "!",
  info: "i",
  question: "?",
};

const noticeIdFrom = (opts) => {
  const raw = `${opts?.icon || "info"}|${opts?.title || ""}|${opts?.text || ""}`;
  let hash = 0;
  for (let i = 0; i < raw.length; i += 1) {
    hash = (hash * 31 + raw.charCodeAt(i)) | 0;
  }
  return `app-notice-${Math.abs(hash)}`;
};

const Message = ({ title, text }) => (
  <div className="hot-toast__message">
    {title ? <strong className="hot-toast__title">{title}</strong> : null}
    {text ? <span className="hot-toast__text">{text}</span> : null}
  </div>
);

const fireNotice = (opts) => {
  const { title, text, icon, timer } = opts;
  const message = <Message title={title} text={text} />;
  const duration = timer || 3200;
  const id = noticeIdFrom(opts);

  if (icon === "success") {
    toast.success(message, { duration, id });
  } else if (icon === "error") {
    toast.error(message, { duration, id });
  } else {
    toast(message, {
      duration,
      id,
      icon: TOAST_ICON[icon] || TOAST_ICON.info,
    });
  }

  return Promise.resolve({
    isConfirmed: true,
    isDismissed: false,
    isDenied: false,
  });
};

const Dialog = ({ opts, onSettle }) => {
  const {
    title,
    text,
    icon = "question",
    input,
    inputLabel,
    inputPlaceholder,
    inputValue = "",
    inputValidator,
    html,
    imageUrl,
    imageAlt = "Preview",
    imageWidth,
    imageHeight,
    confirmButtonText = "Yes, Continue",
    cancelButtonText = "Cancel",
    confirmButtonColor,
    cancelButtonColor,
    showCancelButton = false,
  } = opts;

  const [value, setValue] = useState(inputValue);
  const [issue, setIssue] = useState("");
  const [validating, setValidating] = useState(false);
  const fieldRef = useRef(null);
  const Icon = ICON_COMPONENTS[icon] || ICON_COMPONENTS.question;

  useEffect(() => {
    const id = window.setTimeout(() => fieldRef.current?.focus(), 40);
    return () => window.clearTimeout(id);
  }, []);

  const cancel = () => {
    if (validating) return;
    onSettle({
      isConfirmed: false,
      isDismissed: true,
      isDenied: false,
      value: undefined,
    });
  };

  const confirm = async () => {
    if (validating) return;

    if (input) {
      const trimmed = String(value ?? "").trim();
      if (!trimmed) {
        setIssue("This field is required.");
        return;
      }

      if (inputValidator) {
        try {
          setValidating(true);
          const problem = await inputValidator(value);
          if (problem) {
            setIssue(problem);
            setValidating(false);
            return;
          }
        } catch (error) {
          setIssue(error?.message || "Please check the entered value.");
          setValidating(false);
          return;
        }
      }
    }

    onSettle({
      isConfirmed: true,
      isDismissed: false,
      isDenied: false,
      value: input ? value : true,
    });
  };

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancel();
    }

    if (
      event.key === "Enter" &&
      (input !== "textarea" || event.metaKey || event.ctrlKey)
    ) {
      event.preventDefault();
      confirm();
    }
  };

  return (
    <div className="app-dialog-overlay" role="presentation">
      <div
        className={`app-dialog app-dialog--${icon || "question"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        onKeyDown={onKeyDown}
      >
        <button
          type="button"
          className="app-dialog__close"
          onClick={cancel}
          aria-label="Close dialog"
        >
          <FiX />
        </button>

        <div className="app-dialog__accent" aria-hidden="true" />

        <div className="app-dialog__head">
          <span
            className={`app-dialog__icon app-dialog__icon--${icon || "question"}`}
            aria-hidden="true"
          >
            <Icon />
          </span>

          <div className="app-dialog__copy">
            {title ? (
              <h3 id="app-dialog-title" className="app-dialog__title">
                {title}
              </h3>
            ) : null}
            {text ? <p className="app-dialog__text">{text}</p> : null}
          </div>
        </div>

        {html ? (
          <div
            className="app-dialog__html"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : null}

        {imageUrl ? (
          <div className="app-dialog__media">
            <img
              src={imageUrl}
              alt={imageAlt}
              style={{
                width: imageWidth ? `${imageWidth}px` : undefined,
                height:
                  typeof imageHeight === "number"
                    ? `${imageHeight}px`
                    : imageHeight || undefined,
              }}
            />
          </div>
        ) : null}

        {input ? (
          <div className="app-dialog__field">
            {inputLabel ? (
              <label className="app-dialog__label">{inputLabel}</label>
            ) : null}

            {input === "textarea" ? (
              <textarea
                ref={fieldRef}
                rows={4}
                className="app-dialog__control"
                placeholder={inputPlaceholder}
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                  setIssue("");
                }}
              />
            ) : (
              <input
                ref={fieldRef}
                type="text"
                className="app-dialog__control"
                placeholder={inputPlaceholder}
                value={value}
                onChange={(event) => {
                  setValue(event.target.value);
                  setIssue("");
                }}
              />
            )}

            {issue ? <span className="app-dialog__error">{issue}</span> : null}
          </div>
        ) : null}

        <div className="app-dialog__actions">
          {showCancelButton || input ? (
            <button
              type="button"
              className="app-dialog__btn app-dialog__btn--cancel"
              style={
                cancelButtonColor
                  ? {
                      background: cancelButtonColor,
                      borderColor: cancelButtonColor,
                    }
                  : undefined
              }
              onClick={cancel}
              disabled={validating}
            >
              {cancelButtonText}
            </button>
          ) : null}

          <button
            type="button"
            className={`app-dialog__btn app-dialog__btn--confirm ${
              confirmButtonColor ? "app-dialog__btn--custom" : ""
            }`}
            style={
              confirmButtonColor
                ? { "--dialog-confirm-bg": confirmButtonColor }
                : undefined
            }
            onClick={confirm}
            disabled={validating}
          >
            {validating ? (
              <span className="app-dialog__loading" aria-label="Please wait" />
            ) : null}
            {confirmButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

let dialogContainer = null;
let dialogRoot = null;
let activeDialogResolve = null;

const ensureDialogRoot = () => {
  if (!dialogContainer) {
    dialogContainer = document.createElement("div");
    dialogContainer.id = "app-global-dialog-root";
    document.body.appendChild(dialogContainer);
    dialogRoot = createRoot(dialogContainer);
  }
};

const hideDialog = () => {
  if (dialogRoot) {
    dialogRoot.render(null);
  }
  document.body.classList.remove("app-dialog-open");
};

const dismissActiveDialog = () => {
  if (activeDialogResolve) {
    const resolve = activeDialogResolve;
    activeDialogResolve = null;
    hideDialog();
    resolve({
      isConfirmed: false,
      isDismissed: true,
      isDenied: false,
      value: undefined,
    });
  } else {
    hideDialog();
  }
};

const fireDialog = (opts) => {
  // Strict singleton: if any previous confirmation is still open, dismiss it
  // before showing the next one. This fixes repeated/stacked dialogs globally.
  dismissActiveDialog();
  ensureDialogRoot();
  document.body.classList.add("app-dialog-open");

  return new Promise((resolve) => {
    activeDialogResolve = resolve;

    const settle = (payload) => {
      if (activeDialogResolve !== resolve) return;
      activeDialogResolve = null;
      hideDialog();
      resolve(payload);
    };

    dialogRoot.render(<Dialog opts={opts} onSettle={settle} />);
  });
};

const Swal = {
  fire: (a, b, c) => {
    if (typeof a === "string") {
      return fireNotice({ title: a, text: b, icon: c });
    }

    const opts = a || {};
    return opts.showCancelButton || opts.input || opts.imageUrl
      ? fireDialog(opts)
      : fireNotice(opts);
  },

  close: () => {
    dismissActiveDialog();
    toast.dismiss();
  },
};

export default Swal;
